import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Image,
  SimpleGrid,
  Icon,
  useColorModeValue,
  Divider,
  List,
  ListItem,
  ListIcon,
  Badge,
  Grid,
  GridItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaPlane,
  FaCar,
  FaIndustry,
  FaDollarSign,
  FaCube,
  FaClock,
  FaMicrochip,
  FaCog,
  FaChartLine,
  FaBolt,
  FaRocket,
  FaWind,
} from 'react-icons/fa';
import Footer from '../Footer';

const SupersonicWedgeFlow = () => {
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const sectionBg = useColorModeValue('gray.50', 'gray.800');
  const [animationType, setAnimationType] = useState('none'); // 'none', 'pressure', 'velocity'

  return (
    <Box minH="100vh" bg="gray.50">
      <Helmet>
        <title>Supersonic Wedge Flow Simulation Around Complex Geometries | SRS Blog</title>
        <meta name="description" content="High-fidelity supersonic wedge flow simulation: oblique shocks and industrial-scale flow physics around complex geometries, GPU-accelerated in the cloud." />
        <link rel="canonical" href="https://srs.numericalai.net/blog/supersonic-wedge-flow" />
        <meta property="og:title" content="Supersonic Wedge Flow Simulation Around Complex Geometries | SRS Blog" />
        <meta property="og:description" content="High-fidelity supersonic wedge flow simulation: oblique shocks and industrial-scale flow physics around complex geometries, GPU-accelerated in the cloud." />
        <meta property="og:url" content="https://srs.numericalai.net/blog/supersonic-wedge-flow" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="SRS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Supersonic Wedge Flow Simulation Around Complex Geometries | SRS Blog" />
        <meta name="twitter:description" content="High-fidelity supersonic wedge flow simulation: oblique shocks and industrial-scale flow physics around complex geometries, GPU-accelerated in the cloud." />
      </Helmet>
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200">
        <Container maxW="7xl" py={4}>
          <HStack justify="space-between">
            <HStack 
              spacing={1} 
              cursor="pointer" 
              onClick={() => navigate('/')}
              _hover={{ opacity: 0.8 }} 
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: "scale(0.98)" }}
            >
              <Image 
                src="/simple-logo.png" 
                alt="SRS Logo" 
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <Button
              leftIcon={<FaArrowLeft />}
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </HStack>
        </Container>
      </Box>

      <Container maxW="5xl" py={12}>
        <VStack spacing={12} align="stretch">
          {/* Hero Section */}
          <Box textAlign="center">
            <Badge colorScheme="orange" fontSize="md" mb={4} px={3} py={1}>
              Supersonic Flow
            </Badge>
            <Heading 
              as="h1"
              size="2xl" 
              color="brand.500" 
              mb={6}
              fontWeight="bold"
            >
              Supersonic Wedge Flow Simulation
            </Heading>
            <Heading 
              size="lg" 
              color="gray.600" 
              mb={6}
              fontWeight="medium"
            >
              High-Fidelity Flow Simulation Around Complex Geometries
            </Heading>
            
            <Text 
              fontSize="xl" 
              color="gray.700" 
              lineHeight="tall"
              mb={6}
              textAlign="left"
            >
              Experience cutting-edge computational fluid dynamics (CFD) powered by NumericalAI, a high-performance simulation framework for industrial-scale flow physics around complex geometries.
            </Text>
            
            <Text 
              fontSize="lg" 
              color="gray.600" 
              lineHeight="tall"
              textAlign="left"
            >
              The example below showcases <strong>pressure and velocity distributions around a wedge geometry</strong> at supersonic conditions (Mach ≈ 1.5) using MFC with immersed boundary methods (IBM) and WENO-based compressible solvers. This simulation demonstrates NumericalAI's capability to handle shock-dominated flows around arbitrary STL geometries without complex grid generation.
            </Text>
          </Box>

          {/* Animation Toggle Buttons */}
          <Box textAlign="center">
            <HStack spacing={4} justify="center" flexWrap="wrap">
              <Button
                size="lg"
                colorScheme={animationType === 'none' ? "brand" : "gray"}
                onClick={() => setAnimationType('none')}
                leftIcon={<FaCube />}
              >
                Static Images
              </Button>
              <Button
                size="lg"
                colorScheme={animationType === 'pressure' ? "brand" : "gray"}
                onClick={() => setAnimationType('pressure')}
                leftIcon={<FaBolt />}
              >
                Pressure Animation
              </Button>
              <Button
                size="lg"
                colorScheme={animationType === 'velocity' ? "brand" : "gray"}
                onClick={() => setAnimationType('velocity')}
                leftIcon={<FaWind />}
              >
                Velocity Animation
              </Button>
            </HStack>
          </Box>

          {/* Images or Animations */}
          {animationType === 'pressure' ? (
            <Box 
              bg={cardBg}
              borderRadius="xl"
              overflow="hidden"
              shadow="md"
              border="1px"
              borderColor={borderColor}
            >
              <Image 
                src={require('../../img/blogs/flow_around_wedge_pressure_animation_NumericalAI.gif')}
                alt="Pressure Animation Around Wedge"
                w="100%"
                objectFit="contain"
                bg="gray.100"
              />
              <Box p={6} bg={sectionBg}>
                <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2} textAlign="center">
                  <strong>Pressure Field Time Evolution</strong>
                </Text>
                <Text fontSize="md" color="gray.600" lineHeight="tall" textAlign="center">
                  Full temporal evolution showing bow shock formation, shock reflection from the wedge surface, and downstream wake development. The oblique shock angle matches theoretical predictions from Rankine-Hugoniot relations for the given Mach number and wedge angle.
                </Text>
              </Box>
            </Box>
          ) : animationType === 'velocity' ? (
            <Box 
              bg={cardBg}
              borderRadius="xl"
              overflow="hidden"
              shadow="md"
              border="1px"
              borderColor={borderColor}
            >
              <Image 
                src={require('../../img/blogs/flow_around_wedge_vel_animation_NumericalAI.gif')}
                alt="Velocity Animation Around Wedge"
                w="100%"
                objectFit="contain"
                bg="gray.100"
              />
              <Box p={6} bg={sectionBg}>
                <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2} textAlign="center">
                  <strong>Velocity Field Time Evolution</strong>
                </Text>
                <Text fontSize="md" color="gray.600" lineHeight="tall" textAlign="center">
                  Velocity magnitude evolution revealing the shock structure, shear layer formation along the wedge surface, and velocity deficit in the wake region. Kelvin-Helmholtz instabilities develop downstream as the flow transitions from laminar to turbulent.
                </Text>
              </Box>
            </Box>
          ) : (
            <Tabs variant="enclosed" colorScheme="brand">
              <TabList bg={sectionBg} borderRadius="xl" p={2}>
                <Tab _selected={{ color: 'brand.500', bg: 'white' }}>
                  <Icon as={FaBolt} mr={2} /> Pressure Field
                </Tab>
                <Tab _selected={{ color: 'brand.500', bg: 'white' }}>
                  <Icon as={FaWind} mr={2} /> Velocity Field
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel p={0}>
                  <Box 
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    shadow="md"
                    border="1px"
                    borderColor={borderColor}
                    mt={4}
                  >
                    <Image 
                      src={require('../../img/blogs/flow_around_wedge_pressure_contour_NumericalAI.jpg')}
                      alt="Pressure Contour Around Wedge"
                      w="100%"
                      objectFit="contain"
                      bg="gray.100"
                    />
                    <Box p={6} bg={sectionBg}>
                      <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                        <strong>Pressure Distribution</strong>
                      </Text>
                      <Text fontSize="md" color="gray.600" lineHeight="tall">
                        Pressure distribution showing strong bow shock formation ahead of the wedge leading edge and downstream wake region. The oblique shock wave creates a sharp pressure jump across the shock front, with peak pressure at the stagnation point. The immersed boundary method captures the wedge geometry without body-fitted grids, while maintaining sharp shock resolution.
                      </Text>
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel p={0}>
                  <Box 
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    shadow="md"
                    border="1px"
                    borderColor={borderColor}
                    mt={4}
                  >
                    <Image 
                      src={require('../../img/blogs/flow_around_wedge_velocity_contour_NumericalAI.jpg')}
                      alt="Velocity Contour Around Wedge"
                      w="100%"
                      objectFit="contain"
                      bg="gray.100"
                    />
                    <Box p={6} bg={sectionBg}>
                      <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                        <strong>Velocity Magnitude Distribution</strong>
                      </Text>
                      <Text fontSize="md" color="gray.600" lineHeight="tall">
                        Velocity field highlighting shock reflections and shear-layer instabilities. The flow decelerates across the bow shock, then accelerates along the wedge surface. Complex shock-boundary layer interactions create vortical structures in the wake, critical for accurate drag and heat transfer predictions.
                      </Text>
                    </Box>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}

          <Divider />

          {/* Simulation Overview */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              Simulation Configuration
            </Heading>
            
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6} mb={8}>
              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaCube} boxSize={6} color="brand.500" />
                    <Heading size="md" color="gray.800">Geometry & Grid</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Formulation:</Text>
                      <Text fontWeight="semibold">2D Cartesian + IBM</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Grid Resolution:</Text>
                      <Text fontWeight="semibold">399 × 199</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Total Cells:</Text>
                      <Text fontWeight="semibold">79,401</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Geometry Input:</Text>
                      <Text fontWeight="semibold">STL Surface</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Wedge Angle:</Text>
                      <Text fontWeight="semibold">15°</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaRocket} boxSize={6} color="orange.500" />
                    <Heading size="md" color="gray.800">Flow Conditions</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Mach Number:</Text>
                      <Text fontWeight="semibold">M ≈ 1.5</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Reynolds Number:</Text>
                      <Text fontWeight="semibold">7.5 × 10⁶</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Flow Regime:</Text>
                      <Text fontWeight="semibold">Supersonic</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Fluid:</Text>
                      <Text fontWeight="semibold">Air (ideal gas)</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Shock Angle:</Text>
                      <Text fontWeight="semibold">~28° (oblique)</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaCog} boxSize={6} color="blue.500" />
                    <Heading size="md" color="gray.800">Numerical Methods</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Spatial Scheme:</Text>
                      <Text fontWeight="semibold">WENO5</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Time Integration:</Text>
                      <Text fontWeight="semibold">RK3-TVD</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Riemann Solver:</Text>
                      <Text fontWeight="semibold">HLLC</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">IBM Method:</Text>
                      <Text fontWeight="semibold">Ghost-cell</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">CFL Number:</Text>
                      <Text fontWeight="semibold">0.3</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaClock} boxSize={6} color="purple.500" />
                    <Heading size="md" color="gray.800">Boundary Conditions</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <Box>
                      <Text fontWeight="semibold" color="gray.700" mb={1}>Inlet (left):</Text>
                      <Text color="gray.600" fontSize="sm">Supersonic inflow (M = 1.5)</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="semibold" color="gray.700" mb={1}>Outlet (right):</Text>
                      <Text color="gray.600" fontSize="sm">Non-reflecting outflow</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="semibold" color="gray.700" mb={1}>Top/Bottom:</Text>
                      <Text color="gray.600" fontSize="sm">Slip walls</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="semibold" color="gray.700" mb={1}>Wedge Surface:</Text>
                      <Text color="gray.600" fontSize="sm">No-slip wall (IBM)</Text>
                    </Box>
                  </VStack>
                </Box>
              </GridItem>
            </Grid>

            <Box bg="orange.50" p={6} borderRadius="xl" border="2px" borderColor="orange.200">
              <HStack spacing={3} mb={3}>
                <Icon as={FaMicrochip} boxSize={6} color="orange.600" />
                <Heading size="md" color="orange.800">Computational Performance</Heading>
              </HStack>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="orange.600">~1 min</Text>
                  <Text fontSize="sm" color="gray.600">Wall-Clock Time</Text>
                </Box>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="orange.600">A100</Text>
                  <Text fontSize="sm" color="gray.600">NVIDIA GPU</Text>
                </Box>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="orange.600">~1 GB</Text>
                  <Text fontSize="sm" color="gray.600">Memory Usage</Text>
                </Box>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">~14×</Text>
                  <Text fontSize="sm" color="gray.600">Speedup vs CPU</Text>
                </Box>
              </SimpleGrid>
            </Box>
          </Box>

          <Divider />

          {/* Immersed Boundary Method */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              Immersed Boundary Method (IBM)
            </Heading>
            
            <VStack spacing={6} align="stretch">
              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Heading size="md" color="gray.800" mb={4}>
                  What is IBM and Why It Matters
                </Heading>
                <Text fontSize="md" color="gray.700" lineHeight="tall" mb={4}>
                  The <strong>Immersed Boundary Method</strong> is a powerful technique that allows simulation of flow around complex geometries without generating body-fitted grids. Instead of conforming the computational mesh to the geometry, IBM:
                </Text>
                <List spacing={3}>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" color="gray.800">Uses Cartesian Background Grids</Text>
                      <Text fontSize="md" color="gray.600" mt={1}>
                        Simple, structured meshes with optimal cache performance and straightforward parallelization — no complex mesh generation required.
                      </Text>
                    </Box>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" color="gray.800">Accepts STL Geometry Directly</Text>
                      <Text fontSize="md" color="gray.600" mt={1}>
                        Import CAD models (STL format) without manual grid generation. Change geometry? Just swap the STL file — no re-meshing.
                      </Text>
                    </Box>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" color="gray.800">Enforces Boundary Conditions via Ghost Cells</Text>
                      <Text fontSize="md" color="gray.600" mt={1}>
                        The wedge surface cuts through the Cartesian grid. Ghost cells inside the solid enforce no-slip conditions through extrapolation, maintaining shock-capturing accuracy.
                      </Text>
                    </Box>
                  </ListItem>
                </List>
              </Box>

              <Box bg="blue.50" p={6} borderRadius="xl" border="2px" borderColor="blue.200">
                <Heading size="md" color="blue.800" mb={3}>
                  Industrial Workflow Benefits
                </Heading>
                <Text fontSize="md" color="blue.900" lineHeight="tall">
                  For industrial users, IBM dramatically reduces simulation turnaround time. Traditional body-fitted meshing can take days for complex geometries and requires expert knowledge. With IBM on NumericalAI, you upload an STL, set flow conditions, and run — all within hours instead of weeks. Perfect for design optimization where dozens of geometry variations must be tested.
                </Text>
              </Box>
            </VStack>
          </Box>

          <Divider />

          {/* Physics Insights */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              Supersonic Flow Physics
            </Heading>
            
            <VStack spacing={6} align="stretch">
              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Heading size="md" color="gray.800" mb={4}>
                  Oblique Shock Formation
                </Heading>
                <Text fontSize="md" color="gray.700" lineHeight="tall" mb={4}>
                  When supersonic flow (M = 1.5) encounters the wedge, an <strong>oblique shock wave</strong> forms at an angle of approximately 28° to the freestream. Key physics:
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <Box>
                    <Text fontWeight="semibold" color="gray.800" mb={2}>
                      <Icon as={FaCheckCircle} color="green.500" mr={2} />
                      Pressure Jump
                    </Text>
                    <Text color="gray.600" fontSize="sm" lineHeight="tall">
                      Pressure increases by a factor of ~2.5 across the shock, creating high loads on the wedge surface. Critical for structural design.
                    </Text>
                  </Box>
                  
                  <Box>
                    <Text fontWeight="semibold" color="gray.800" mb={2}>
                      <Icon as={FaCheckCircle} color="green.500" mr={2} />
                      Temperature Rise
                    </Text>
                    <Text color="gray.600" fontSize="sm" lineHeight="tall">
                      Flow temperature increases across the shock due to compression, creating thermal loads that must be managed in aerospace applications.
                    </Text>
                  </Box>

                  <Box>
                    <Text fontWeight="semibold" color="gray.800" mb={2}>
                      <Icon as={FaCheckCircle} color="green.500" mr={2} />
                      Flow Deflection
                    </Text>
                    <Text color="gray.600" fontSize="sm" lineHeight="tall">
                      Flow turns 15° to align with the wedge surface. The shock angle and deflection angle are related by the θ-β-M relation.
                    </Text>
                  </Box>

                  <Box>
                    <Text fontWeight="semibold" color="gray.800" mb={2}>
                      <Icon as={FaCheckCircle} color="green.500" mr={2} />
                      Entropy Generation
                    </Text>
                    <Text color="gray.600" fontSize="sm" lineHeight="tall">
                      Shock waves are irreversible processes that generate entropy, representing a loss mechanism in propulsion systems.
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>

              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Heading size="md" color="gray.800" mb={4}>
                  Shock-Boundary Layer Interaction
                </Heading>
                <Text fontSize="md" color="gray.700" lineHeight="tall">
                  At Re = 7.5×10⁶, the boundary layer along the wedge surface is thin but highly influential. The shock impingement creates an <strong>adverse pressure gradient</strong> that can cause flow separation in the near-wall region. This phenomenon is critical for:
                </Text>
                <List spacing={2} mt={3}>
                  <ListItem>
                    <Icon as={FaCheckCircle} color="green.500" mr={2} />
                    <Text as="span" fontSize="md" color="gray.700">Supersonic inlet design (preventing flow separation)</Text>
                  </ListItem>
                  <ListItem>
                    <Icon as={FaCheckCircle} color="green.500" mr={2} />
                    <Text as="span" fontSize="md" color="gray.700">Drag prediction (separation increases form drag)</Text>
                  </ListItem>
                  <ListItem>
                    <Icon as={FaCheckCircle} color="green.500" mr={2} />
                    <Text as="span" fontSize="md" color="gray.700">Heat transfer analysis (peaks occur at reattachment points)</Text>
                  </ListItem>
                </List>
              </Box>
            </VStack>
          </Box>

          <Divider />

          {/* Industrial Applications */}
          <Box>
            <Heading size="lg" color="brand.500" mb={6} textAlign="center">
              Industrial Applications
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaPlane} boxSize={8} color="blue.500" />
                  <Heading size="md" color="gray.800">
                    Aerospace Engineering
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Shock–boundary layer interaction studies for supersonic inlet design, wing-body junctions, and control surface effectiveness. Essential for designing efficient supersonic aircraft and reducing wave drag.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaCar} boxSize={8} color="red.500" />
                  <Heading size="md" color="gray.800">
                    Automotive Aerodynamics
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    External aerodynamics around sharp-edged structures like spoilers, diffusers, and A-pillars. Predict flow separation, vortex formation, and pressure distribution for drag reduction and downforce optimization.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaIndustry} boxSize={8} color="orange.500" />
                  <Heading size="md" color="gray.800">
                    Energy & Turbomachinery
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Flow optimization around turbine blades, nozzles, and diffusers. Shock interactions affect efficiency, noise, and structural loading in compressors and turbines operating at transonic and supersonic conditions.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaRocket} boxSize={8} color="purple.500" />
                  <Heading size="md" color="gray.800">
                    Defense & Propulsion
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Missile fin analysis, ramjet/scramjet inlet design, and nozzle flow characterization. Accurate shock prediction is critical for performance, stability, and control authority at high speeds.
                  </Text>
                </VStack>
              </Box>
            </SimpleGrid>
          </Box>

          <Divider />

          {/* Simulation Highlights */}
          <Box bg={cardBg} p={8} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
            <Heading size="lg" color="brand.500" mb={6}>
              Why NumericalAI for Complex Geometry CFD
            </Heading>
            <List spacing={4}>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Robust, scalable parallel performance</strong> — HPC-ready for large-scale industrial computations with double-precision output
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Accurate multiphase and compressible flow modeling</strong> — WENO5 shock-capturing with Riemann solvers for supersonic regimes
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Seamless integration with STL-based CAD geometries</strong> — IBM eliminates weeks of manual meshing work
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Fully customizable simulation workflows</strong> — Python-based configuration for automated parametric studies
                </Text>
              </ListItem>
            </List>
          </Box>

          {/* Business Value */}
          <Box 
            bg="brand.500" 
            color="white" 
            p={8} 
            borderRadius="xl" 
            shadow="xl"
            position="relative"
            overflow="hidden"
          >
            <Box position="relative" zIndex={1}>
              <HStack spacing={3} mb={4}>
                <Icon as={FaDollarSign} boxSize={8} />
                <Heading size="lg">Business Value & ROI</Heading>
              </HStack>
              <Text fontSize="lg" lineHeight="tall" mb={4}>
                NumericalAI democratizes supersonic CFD — what used to require specialized grid generation expertise and dedicated HPC infrastructure is now accessible through an intuitive cloud interface.
              </Text>
              <Text fontSize="lg" lineHeight="tall">
                <strong>Time Savings:</strong> Skip weeks of mesh generation. Upload STL, run simulation, get results — all in under an hour. Perfect for rapid design iterations where dozens of geometries must be evaluated.
              </Text>
            </Box>
            
            {/* Decorative background */}
            <Box
              position="absolute"
              top="-50%"
              right="-10%"
              width="300px"
              height="300px"
              borderRadius="full"
              bg="whiteAlpha.100"
              zIndex={0}
            />
          </Box>

          {/* Call to Action */}
          <Box textAlign="center" py={8}>
            <VStack spacing={4}>
              <Heading size="md" color="gray.700">
                Ready to Simulate Your Complex Geometries?
              </Heading>
              <Text color="gray.600" fontSize="lg" maxW="2xl">
                Experience the power of IBM-enabled CFD on NumericalAI. Upload your STL files, run supersonic simulations, and accelerate your design process.
              </Text>
              <HStack spacing={4} mt={4}>
                <Button
                  colorScheme="brand"
                  size="lg"
                  onClick={() => navigate('/start-simulation')}
                >
                  Start Your Simulation
                </Button>
                <Button
                  variant="outline"
                  colorScheme="brand"
                  size="lg"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                </Button>
              </HStack>
            </VStack>
          </Box>
        </VStack>
      </Container>

      <Footer />
    </Box>
  );
};

export default SupersonicWedgeFlow;